import React, { useState, createContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { useAuth, storage } from '@byscott-io/corebyscott'

import axios from 'axios'
import _ from 'lodash'
import _inflection from 'lodash-inflection';
_.mixin(_inflection);

import { deepCamelizeKeys, deepUnderscoreKeys } from '../modules/KeyTransform'

export const { Provider, Consumer } = createContext({})

export const withUserStore = ComponentWithUserStore => {
  const WrappedComponent = React.forwardRef((props, ref) => {
    return (
      <Consumer>
        {({ userStore }) => <ComponentWithUserStore userStore={userStore} {...props} ref={ref} /> }
      </Consumer>
    )
  })
  
  ComponentWithUserStore.propTypes = {
    userStore: PropTypes.object.isRequired
  }
  
  return WrappedComponent
}

const UserStore = ({ children }) => {
  const [cookies, setCookie] = useCookies(['lastAccountId'])
  const navigate = useNavigate()
  const { user: authUser, signOut } = useAuth()

  const [env] = useState()

  // Token is owned by corebyscott (stored in a cookie). Expose it in the legacy
  // "Bearer <jwt>" shape the domain API calls + consumers already expect.
  const tokenHeader = () => {
    const t = storage.getToken()
    return t ? `Bearer ${t}` : null
  }
  const [bearerToken, setBearerToken] = useState(tokenHeader)
  const [user, setUser] = useState()
  const [accountId, setAccountId] = useState()
  const [account, setAccount] = useState()
  const [accounts, setAccounts] = useState()
  const [currentAccountsUser, setCurrentAccountsUser] = useState()
  const [settings, setSettings] = useState()
  const [lex, setLex] = useState({})

  // Sync identity + token from corebyscott's AuthProvider. On sign-out (authUser
  // becomes null) clear account-scoped state so a later sign-in reloads fresh.
  useEffect(() => {
    setBearerToken(tokenHeader())
    if (authUser) {
      setUser(deepCamelizeKeys(authUser))
    } else {
      setUser(null)
      setAccounts(undefined)
      setAccount(undefined)
      setCurrentAccountsUser(undefined)
      setSettings(undefined)
      setLex({})
    }
  }, [authUser])

  useEffect(() => {
    if (account) {
      setLex(Object.fromEntries(account.lexicons.map(l => [l.name, l.value])))
    }
    if (account && user) {
      setCurrentAccountsUser(account.accountsUsers.find((au) => au.userId === user.id))
      indexSettings()
    }
  }, [account, user])

  useEffect(() => {
    if (bearerToken && user && !accounts) {
      // After core login we land on bare /app; redirect to /app/:accountId then.
      // On a refresh already at /app/:id, the account-selection effect picks by URL.
      const atBareApp = !/\/app\/\d+/.test(window.location.pathname)
      getAccounts(user, atBareApp)
    }
  }, [bearerToken, user, accounts])

  useEffect(() => {
    if (accounts) {
      // Use accountId from URL, then lastAccountId from cookie, then first account
      const lastAccountId = cookies.lastAccountId
      const aid = accountId || lastAccountId || accounts?.[0]?.id
      if (account?.id !== aid) {
        const selectedAccount = accounts.find(a => a.id === aid)
        setAccount(selectedAccount)
      }
    }
  }, [accountId, accounts, cookies.lastAccountId])

  // Realtime cable connection is handled by <PresenceProvider> (whereiswaldo) at the
  // app root; it reads the JWT from corebyscott storage, so no per-user wiring here.

  const indexSettings = () => {
    index('settings', null, ({settings}) => {
      setSettings(settings)
    })
  }

  const getSetting = (type, id, name) => {
    if (settings) {
      return settings?.find(r => r.recordType === type && r.recordId === id && r.name === name)
    }
  }

  const setSetting = (name, value, recordType, recordId, callback) => {
    const existingSetting = getSetting(recordType, recordId, name)
    
    if (existingSetting) {
      // Only update if value has changed
      if (existingSetting.value === value) {
        if (callback) callback(value)
        return
      }
      
      // Update via API and wait for response
      update(existingSetting, 'setting', { value }, ({setting}) => {
        setSettings([...(settings || []).filter(s => s.id !== setting.id), setting])
        if (callback) callback(value)
      })
    } else {
      // Create via API and wait for response
      create('setting', {name, value, recordType, recordId}, ({setting}) => {
        setSettings([...(settings || []), setting])
        if (callback) callback(value)
      })
    }
  }

  const getUserSetting = (name) => {
    return getSetting('User', user?.id, name)?.value
  }

  const getAccountsUserSetting = (name) => {
    return getSetting('AccountsUser', currentAccountsUser?.id, name)?.value
  }

  const getAccountSetting = (name) => {
    return getSetting('Account', account?.id, name)?.value
  }

  const setUserSetting = (name, value, userId, callback) => {
    setSetting(name, value, 'User', userId, callback)
  }

  const setAccountSetting = (name, value, accountId, callback) => {
    setSetting(name, value, 'Account', accountId, callback)
  }

  const setAccountsUserSetting = (name, value, callback) => {
    if (!currentAccountsUser?.id) {
      if (callback) callback()
      return
    }
    setSetting(name, value, 'AccountsUser', currentAccountsUser.id, callback)
  }

  const getFeature = (feature, key) => {
    const f = currentAccountsUser?.features?.find(f => f.name === feature)
    if (!f || !f.value) return null
    const v = JSON.parse(f.value)
    return key ? v?.[key] : v
  }

  const findPermissions = (type, perm) => {
    const upperType = _.upperFirst(type)
    const permType = currentAccountsUser?.defaultPermissions?.find(p => p.recordType === upperType)
    return permType?.[perm]
  }

  const canCreate = (type) => {
    return findPermissions(type, 'canCreate')
  }

  const canRead = (type, record) => {
    return findPermissions(type, 'canRead') && record.permissions && record.permissions.canRead
  }

  // Removed unused argument "columns" from function "canUpdate"
  const canUpdate = (type, record) => {
    return findPermissions(type, 'canUpdate') && record.permissions && record.permissions.canUpdate
  }

  const canDelete = (type, record) => {
    return findPermissions(type, 'canDelete') && record.permissions && record.permissions.canDelete
  }

  const getAccountsUserById = (accountsUserId) => {
    return account?.accountsUsers.find(au => au.id === accountsUserId)
  }

  const get = (recordId, entity, callback) => {
    axios.get(`/api/v1/${_(_.snakeCase(entity)).pluralize()}/${recordId}`,
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json', JWT_AUD: 'WebApp', Authorization: bearerToken } }
    ).then(res => {
      const record = deepCamelizeKeys(res.data[entity])
      // Add recordType for consistent object references
      if (record && typeof record === 'object') {
        record.recordType = _.upperFirst(_.camelCase(entity)) // PascalCase for Rails
      }
      callback({ [_.camelCase(entity)]: record, headers: res.headers })
    })
  }

  const index = (entity, conditions, callback) => {
    const config = { 
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', JWT_AUD: 'WebApp', Authorization: bearerToken }
    }
    
    // Add query parameters if conditions are provided
    if (conditions) {
      config.params = conditions
    }
    
    const url = `/api/v1/${_(_.snakeCase(entity)).pluralize()}`
    
    axios.get(url, config
    ).then(res => {
      const snakeEntity = _(_.snakeCase(entity)).pluralize()
      
      // Add recordType to each record for consistent object references
      const records = deepCamelizeKeys(res.data[snakeEntity])
      const recordType = _.upperFirst(_.camelCase(_.singularize(entity))) // PascalCase for Rails
      if (Array.isArray(records)) {
        records.forEach(record => {
          if (record && typeof record === 'object') {
            record.recordType = recordType
          }
        })
      } else if (records && typeof records === 'object') {
        records.recordType = recordType
      }
      
      callback({ [_.camelCase(entity)]: records, headers: res.headers })
    }).catch(error => {
      console.error('API error for', entity, ':', error)
    })
  }

  const update = (record, entity, payload, saveCallback) => {
    axios.put(`/api/v1/${_(_.snakeCase(entity)).pluralize()}/${record.id}`,
      { [_.snakeCase(entity)]: deepUnderscoreKeys(payload) },
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json', JWT_AUD: 'WebApp', Authorization: bearerToken } }
    ).then(res => {
      const updatedData = deepCamelizeKeys(res.data)
      // Add recordType to updated records for consistent object references
      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] && typeof updatedData[key] === 'object' && !Array.isArray(updatedData[key])) {
          updatedData[key].recordType = _.upperFirst(_.camelCase(key)) // PascalCase for Rails
        }
      })
      saveCallback({ ...updatedData, headers: res.headers })
    })
  }

  const create = (entity, payload, saveCallback) => {
    axios.post(`/api/v1/${_(_.snakeCase(entity)).pluralize()}`,
      { [_.snakeCase(entity)]: deepUnderscoreKeys(payload) },
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json', JWT_AUD: 'WebApp', Authorization: bearerToken } }
    ).then(res => {
      const record = deepCamelizeKeys(res.data[entity])
      // Add recordType for consistent object references
      if (record && typeof record === 'object') {
        record.recordType = _.upperFirst(_.camelCase(entity)) // PascalCase for Rails
      }
      saveCallback({ [_.camelCase(entity)]: record, headers: res.headers })
    })
  }

  const destroy = (record, entity, callback) => {
    axios.delete(`/api/v1/${_(_.snakeCase(entity)).pluralize()}/${record.id}`,
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json', JWT_AUD: 'WebApp', Authorization: bearerToken } }
    ).then(res => {
      callback({ [_.camelCase(entity)]: deepCamelizeKeys(res.data[entity]), headers: res.headers })
    })
  }

  const switchAccounts = (accountId, callback) => {
    if (accountId !== account?.id) {
      setAccountId(accountId)
      // Store last selected account in cookie for sticky behavior
      setCookie('lastAccountId', accountId, { path: '/' })
    }
    if (callback) callback()
  }

  // Reset account-related state for testing (keeps user/auth intact)
  const resetAccountState = () => {
    setAccountId(undefined)
    setAccount(undefined)
    setAccounts(undefined)
    setCurrentAccountsUser(undefined)
    setSettings(undefined)
    setLex({})
  }

  const createAccount = (token, user, name, accountType) => {
    axios.post('/api/v1/accounts',
      { account: { name, account_type: accountType }, user: { user_id: user.id } },
      { headers: { 'Content-Type': 'application/json', JWT_AUD: 'WebApp', Authorization: token || bearerToken } }
    ).then(res => {
      const accounts = deepCamelizeKeys(res.data.accounts)
      setAccounts(accounts)
      // Navigate to the new account's app page after creation and store it as last selected
      if (accounts && accounts.length > 0) {
        const newAccountId = accounts[0].id
        setCookie('lastAccountId', newAccountId, { path: '/' })
        navigate(`/app/${newAccountId}`, { replace: true })
      }
    })
  }

  const getAccounts = (user, redirect = true) => {
    axios.get(`/api/v1/users/${user.id}/accounts`,
      { headers: { 'Content-Type': 'application/json', JWT_AUD: 'WebApp', Authorization: bearerToken } }
    ).then(res => {
      const accounts = deepCamelizeKeys(res.data.accounts)
      setAccounts(accounts)
      // Use sticky account selection from cookie, or fall back to first account
      if (redirect && accounts && accounts.length > 0) {
        const lastAccountId = cookies.lastAccountId
        const targetAccount = accounts.find(a => a.id === lastAccountId) || accounts[0]
        navigate(`/app/${targetAccount.id}`, { replace: true })
      }
    })
  }

  const logout = async () => {
    try {
      await signOut()
    } catch {
      // ignore — the authUser→null effect clears local state; redirect regardless
    }
    navigate('/', { replace: true })
  }

  const updateAccount = (account, name, accountType) => {
    try {
      update(account, 'account', { name, accountType }, ({accounts}) => {
        setAccounts(accounts)
        setAccount(accounts.find(a => a.id === account.id))
      })
    } catch {
      // Silent catch
    }
  }

  const updateUser = (user, firstName, lastName, nickname, email) => {
    if (firstName && lastName && email) {
      try {
        update(user, 'user', { first_name: firstName, last_name: lastName, nickname, email }, ({user}) => {
          setUser(user)
        })
      } catch (error) {
        console.warn('Failed to update user profile:', error)
      }
    }
  }

  const findRecord = (entity, id, callback = null) => {
    if (account && account[entity]) {
      const result = account[entity].find(r => r.id === id)
      if (callback) callback(result)
      return result
    } else if (bearerToken && id != null && id !== undefined) {
      get(id, entity, (data) => callback(data[entity]))
    } else {
      // Return null if no account or bearer token is available, or if ID is invalid
      if (callback) callback(null)
      return null
    }
  }

  const getRecords = (type, sortBy, filterBy, callback) => {
    if (account && account[type]) {
      let list = account[type]
      if (filterBy) list = list.filter(filterBy)
      if (sortBy) list = list.sort(sortBy)
      if (callback) callback(list)
      return list
    } else if (bearerToken) {
      index(type, null, (data) => {
        let list = data[type]
        if (filterBy) list = list.filter(filterBy)
        if (sortBy) list = list.sort(sortBy)
        if (callback) callback(list)
      })
    } else {
      // Return empty array if no account or bearer token is available
      const emptyList = []
      if (callback) callback(emptyList)
      return emptyList
    }
  }

  const createGoal = (name, description, priorityId, strategyId, accountId, callback) => {
    try {
      create('goal', { name, description, priorityId, strategyId, accountId, accountsUserId: currentAccountsUser.id }, ({goal}) => {
        setAccount({...account, goals: [...(account.goals || []), goal]})
        if (callback) callback(goal)
      })
    } catch {
      // Silent catch
    }
  }

  const getGoals = (callback) => index('goals', null, callback)

  const getGoal = (id, callback) => {
    return findRecord('goals', id, callback)
  }

  const updateGoal = (goal, callback) => {
    try {
      update(goal, 'goal', goal, ({goal}) => {
        setAccount({...account, goals: [...(account.goals || []).filter(g => g.id !== goal.id), goal]})
        if (callback) callback(goal)
      })
    } catch {
      // Silent catch
    }
  }

  const destroyGoal = (goal, callback) => {
    try {
      destroy(goal, 'goal', ({goal}) => {
        setAccount({...account, goals: account.goals.filter(g => g.id !== goal.id)})
        if (callback) callback(goal)
      })
    } catch {
      // Silent catch
    }
  }

  const createStrategy = (name, description, teamId, priorityId, callback) => {
    try {
      create('strategy', { name, description, teamId, priorityId }, ({strategy}) => {
        setAccount({...account, strategies: [...(account.strategies || []), strategy]})
        if (callback) callback(strategy)
      })
    } catch {
      // Silent catch
    }
  }

  const getStrategies = (callback) => index('strategies', null, callback)

  const getStrategy = (id, callback) => {
    return findRecord('strategies', id, callback)
  }

  const updateStrategy = (strategy, callback) => {
    try {
      update(strategy, 'strategy', strategy, ({strategy}) => {
        setAccount({...account, strategies: [...(account.strategies || []).filter(s => s.id !== strategy.id), strategy]})
        if (callback) callback(strategy)
      })
    } catch {
      // Silent catch
    }
  }

  const destroyStrategy = (strategy, callback) => {
    try {
      destroy(strategy, 'strategy', ({strategy}) => {
        setAccount({...account, strategies: account.strategies.filter(s => s.id !== strategy.id)})
        if (callback) callback(strategy)
      })
    } catch {
      // Silent catch
    }
  }

  const createPriority = (name, description, accountId, callback) => {
    try {
      create('priority', { name, description, accountId }, ({priority}) => {
        setAccount({...account, priorities: [...(account.priorities || []), priority]})
        if (callback) callback(priority)
      })
    } catch {
      // Silent catch
    }
  }

  const getPriorities = (callback) => index('priorities', null, callback)

  const getPriority = (id, callback) => {
    return findRecord('priorities', id, callback)
  }

  const updatePriority = (priority, callback) => {
    try {
      update(priority, 'priority', priority, ({priority}) => {
        setAccount({...account, priorities: [...(account.priorities || []).filter(p => p.id !== priority.id), priority]})
        if (callback) callback(priority)
      })
    } catch {
      // Silent catch
    }
  }

  const destroyPriority = (priority, callback) => {
    try {
      destroy(priority, 'priority', ({priority}) => {
        setAccount({...account, priorities: account.priorities.filter(p => p.id !== priority.id)})
        if (callback) callback(priority)
      })
    } catch {
      // Silent catch
    }
  }

  const createTeam = (name, description, accountId, callback) => {
    try {
      create('team', { name, description, accountId }, ({team}) => {
        setAccount({...account, teams: [...(account.teams || []), team]})
        if (callback) callback(team)
      })
    } catch {
      // Silent catch
    }
  }

  const getTeams = (callback) => {
    return getRecords('teams', (a, b) => ((a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : (b.name.toLowerCase() > a.name.toLowerCase()) ? -1 : 0), null, callback)
  }

  const getTeam = (id, callback) => {
    return findRecord('teams', id, callback)
  }

  const updateTeam = (team, callback) => {
    try {
      update(team, 'team', team, ({team}) => {
        setAccount({...account, teams: [...(account.teams || []).filter(t => t.id !== team.id), team]})
        if (callback) callback(team)
      })
    } catch {
      // Silent catch
    }
  }

  const destroyTeam = (team, callback) => {
    try {
      destroy(team, 'team', ({team}) => {
        setAccount({...account, teams: account.teams.filter(t => t.id !== team.id)})
        if (callback) callback(team)
      })
    } catch {
      // Silent catch
    }
  }
  const createInvitation = (email, accountId, accountRole, teamId, teamRole, orgNickname = null, callback = null) => {
    // Handle case where orgNickname is actually the callback (backward compatibility)
    if (typeof orgNickname === 'function') {
      callback = orgNickname
      orgNickname = null
    }
    
    try {
      const invitationData = { 
        email, 
        accountId, 
        accountRole, 
        teamId, 
        teamRole, 
        inviterId: currentAccountsUser.id 
      }
      
      // Only include orgNickname if it's provided
      if (orgNickname) {
        invitationData.orgNickname = orgNickname
      }
      
      create('invitation', invitationData, ({invitation}) => {
        setAccount({...account, invitations: [...(account.invitations || []), invitation]})
        if (callback) callback(invitation)
      })
    } catch {
      // Silent catch
    }
  }
  const getInvitations = (callback) => {
    return getRecords('invitations', (a, b) => ((a.email.toLowerCase() > b.email.toLowerCase()) ? 1 : (b.email.toLowerCase() > a.email.toLowerCase()) ? -1 : 0), null, callback)
  }
  const getInvitation = (id, callback) => {
    return findRecord('invitations', id, callback)
  }
  const updateInvitation = (invitation, callback) => {
    try {
      update(invitation, 'invitation', invitation, ({invitation}) => {
        setAccount({...account, invitations: account.invitations.map(i => i.id === invitation.id ? invitation : i)})
        if (callback) callback(invitation)
      })
    } catch {
      // Silent catch
    }
  }
  const destroyInvitation = (invitation, callback) => {
    try {
      destroy(invitation, 'invitation', ({invitation}) => {
        setAccount({...account, invitations: account.invitations.filter(i => i.id !== invitation.id)})
        if (callback) callback(invitation)
      })
    } catch {
      // Silent catch
    }
  }
  const updateAccountsUser = (accountsUser, callback) => {
    try {
      update(accountsUser, 'accounts_user', accountsUser, ({accountsUser}) => {
        setAccount({...account, accountsUsers: account.accountsUsers.map(au => au.id === accountsUser.id ? accountsUser : au)})
        if (callback) callback(accountsUser)
      })
    } catch {
      // Silent catch
    }
  }
  const destroyAccountsUser = (accountsUser, callback) => {
    try {
      destroy(accountsUser, 'accounts_user', ({accountsUser}) => {
        setAccount({...account, accountsUsers: account.accountsUsers.filter(au => au.id !== accountsUser.id)})
        if (callback) callback(accountsUser)
      })
    } catch {
      // Silent catch
    }
  }

  const createTask = (name, description, goalId, callback) => {
    try {
      // Combine name and description into content field as required by Task model
      const content = description && description.trim() ? `${name}\n${description}` : name
      
      const taskData = {
        content,
        goalId,
        accountId: account.id,
        accountsUserId: currentAccountsUser.id
      }
      
      create('task', taskData, ({task}) => {
        // Add the task to the account's tasks array
        const updatedAccount = {
          ...account,
          tasks: account.tasks ? [...account.tasks, task] : [task]
        }
        setAccount(updatedAccount)
        if (callback) callback(task)
      })
    } catch {
      // Silent catch
    }
  }

  const createJournalEntry = (content, accountId, goalId, taskId, callback) => {
    try {
      const journalData = {
        content,
        accountId,
        goalId,
        accountsUserId: currentAccountsUser.id
      }
      
      // Add taskId only if provided (it's optional)
      if (taskId) {
        journalData.taskId = taskId
      }
      
      create('journalEntry', journalData, ({journalEntry}) => {
        setAccount({...account, journalEntries: [...(account.journalEntries || []), journalEntry]})
        if (callback) callback(journalEntry)
      })
    } catch {
      // Silent catch
    }
  }

  const getJournalEntries = (journalable, conditions, callback) => index('journalEntries', conditions, callback)

  const getJournalEntry = (id, callback) => {
    return findRecord('journalEntries', id, callback)
  }

  const updateJournalEntry = (journalEntry, callback) => {
    try {
      update(journalEntry, 'journalEntry', journalEntry, ({journalEntry}) => {
        setAccount({...account, journalEntries: [...(account.journalEntries || []).filter(j => j.id !== journalEntry.id), journalEntry]})
        if (callback) callback(journalEntry)
      })
    } catch {
      // Silent catch
    }
  }

  const destroyJournalEntry = (journalEntry, callback) => {
    try {
      destroy(journalEntry, 'journalEntry', ({journalEntry}) => {
        setAccount({...account, journalEntries: account.journalEntries.filter(j => j.id !== journalEntry.id)})
        if (callback) callback(journalEntry)
      })
    } catch {
      // Silent catch
    }
  }

  const createComment = (content, commentable, callback) => {
    try {
      create('comment', { 
        content, 
        commentableId: commentable.id, 
        commentableType: commentable.recordType,
        accountId: account?.id,
        accountsUserId: currentAccountsUser?.id
      }, ({comment}) => {
        if (callback) callback(comment)
      })
    } catch {
      // Silent catch
    }
  }

  const getComments = (commentable, conditions, callback) => {
    // Query by root_commentable to get all comments in the thread with a single request
    const params = {
      ...conditions,
      root_commentable_type: commentable.recordType,
      root_commentable_id: commentable.id
    }
    return index('comments', params, callback)
  }

  const getComment = (id, callback) => {
    return findRecord('comments', id, callback)
  }

  const updateComment = (comment, callback) => {
    try {
      update(comment, 'comment', comment, ({comment}) => {
        setAccount({...account, comments: [...(account.comments || []).filter(c => c.id !== comment.id), comment]})
        if (callback) callback(comment)
      })
    } catch {
      // Silent catch
    }
  }

  const destroyComment = (comment, callback) => {
    try {
      destroy(comment, 'comment', ({comment}) => {
        setAccount({...account, comments: account.comments.filter(c => c.id !== comment.id)})
        if (callback) callback(comment)
      })
    } catch {
      // Silent catch
    }
  }

  const updateLexicon = (lexicons) => {
    axios.put(`/api/v1/accounts/${account.id}/lexicons`,
      { lexicons },
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json', JWT_AUD: 'WebApp', Authorization: bearerToken } }
    ).then(res => {
      setLex({...lex, ...Object.fromEntries(res.data.lexicons.map(l => [l.name, l.value]))})
    })
  }

  const createTag = (name, accountId, callback) => {
    create('tag', { name, account_id: accountId }, callback)
  }

  const createTagging = (taggableType, taggableId, tagId, accountId, callback) => {
    create('tagging', {
      taggable_type: taggableType,
      taggable_id: taggableId,
      tag_id: tagId,
      account_id: accountId
    }, callback)
  }

  const destroyTagging = (taggingId, callback) => {
    destroy(taggingId, 'tagging', callback)
  }

  const appUrl = (path) => {
    path = path ? '/' + path : ''
    return '/app/' + account?.id + path
  }

  const appNavigate = (path = null) => navigate(appUrl(path))


  const userStore = () => {
    return {
      env,
      appNavigate,
      switchAccounts,
      logout,
      createAccount,
      getAccounts,
      updateAccount,
      setAccountId,
      accountId,
      updateAccountsUser,
      destroyAccountsUser,
      // exportAccount
      updateLexicon,
      getAccountsUserById,
      getAccountSetting,
      setAccountSetting,
      getUserSetting,
      setUserSetting,
      getAccountsUserSetting,
      setAccountsUserSetting,
      getFeature,
      resetAccountState,
      // setShowTour
      // openModal

      user,
      setUser,
      account,
      accounts,
      currentAccountsUser,
      bearerToken,
      lex,
      updateUser,
      // index,
      // get,
      // update,
      // create,
      // destroy,
      findRecord,
      getRecords,
      canCreate,
      canRead,
      canUpdate,
      canDelete,

      createComment,
      getComments,
      getComment,
      updateComment,
      destroyComment,

      // createFollow
      // getFollows
      // getFollow
      // updateFollow
      // destroyFollow

      // createImport
      // getImports
      // getImport
      // updateImport
      // destroyImport

      createJournalEntry,
      getJournalEntries,
      getJournalEntry,
      updateJournalEntry,
      destroyJournalEntry,

      // createMention
      // getMentions
      // getMention
      // updateMention
      // destroyMention

      createTag,
      // getTags
      // getTag
      // updateTag
      // destroyTag

      createTagging,
      // getTaggings
      // getTagging
      // updateTagging
      destroyTagging,

      createTask,
      // getTasks
      // getTask
      // updateTask
      // destroyTask

      createStrategy,
      getStrategies,
      getStrategy,
      updateStrategy,
      destroyStrategy,

      createPriority,
      getPriorities,
      getPriority,
      updatePriority,
      destroyPriority,

      createGoal,
      getGoals,
      getGoal,
      updateGoal,
      destroyGoal,

      createTeam,
      getTeams,
      getTeam,
      updateTeam,
      destroyTeam,

      createInvitation,
      getInvitations,
      getInvitation,
      updateInvitation,
      destroyInvitation
    }
  }

  return (
    <Provider value={{ userStore: userStore() }}>
      {children}
    </Provider>
  )
}

UserStore.propTypes = {
  children: PropTypes.node.isRequired
}

export default UserStore
